import { useEffect } from 'react';

export type SwipeToReplyOptions = {
  onReply: (messageIndex: number) => void;
  messages: any[];
  enabled?: boolean;
};

/**
 * Custom hook to handle swipe-to-reply gesture on message elements.
 *
 * Attaches touch and mouse event listeners to message containers to detect
 * horizontal swipe gestures. When a user swipes a message to the right beyond
 * a threshold, it triggers the reply action.
 *
 * @param options - Configuration for swipe behavior
 * @param options.onReply - Callback when swipe threshold is reached
 * @param options.messages - Array of messages to attach swipe listeners to
 * @param options.enabled - Whether swipe is enabled (default: true)
 */
export function useSwipeToReply({ onReply, messages, enabled = true }: SwipeToReplyOptions) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const messageBoxHtmlElements = document.querySelectorAll<HTMLElement>('[data-slot=message]');
    const cleanups: Array<() => void> = [];

    const getClientX = (event: TouchEvent | MouseEvent) => {
      if ('touches' in event) {
        return event.touches[0]?.clientX ?? 0;
      }
      return event.clientX;
    };

    Array.from(messageBoxHtmlElements).forEach((element, index) => {
      const container = element;
      const messageBox = container.querySelector<HTMLElement>('[data-slot=message-bubble]');
      if (!messageBox) {
        return;
      }

      if (messages[index]?.is_deleted) {
        return;
      }

      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let currentY = 0;
      let isDragging = false;
      let isHorizontalSwipe = false;

      const maxTranslate = 64; // Max pixels message can be swiped
      const triggerThreshold = 56; // Pixels needed to trigger reply
      const activateThreshold = 10; // Pixels to show reply icon

      const reset = () => {
        isDragging = false;
        isHorizontalSwipe = false;
        currentX = 0;
        currentY = 0;
        messageBox.style.transition = 'transform 0.18s ease-out';
        messageBox.style.transform = 'translateX(0px)';
        delete messageBox.dataset.swipe;
      };

      const onStart = (event: TouchEvent | MouseEvent) => {
        if (event instanceof MouseEvent && event.button !== 0) {
          return;
        }

        startX = getClientX(event);
        if (event instanceof TouchEvent) {
          startY = event.touches[0]?.clientY ?? 0;
        } else {
          startY = event.clientY;
        }
        currentX = startX;
        currentY = startY;
        isDragging = true;
        isHorizontalSwipe = false;
        messageBox.style.transition = 'none';
      };

      const onMove = (event: TouchEvent | MouseEvent) => {
        if (!isDragging) {
          return;
        }

        currentX = getClientX(event);
        currentY = event instanceof TouchEvent ? (event.touches[0]?.clientY ?? 0) : event.clientY;
        const deltaXRaw = currentX - startX;
        const deltaY = Math.abs(currentY - startY);

        const deltaX = Math.max(0, Math.min(deltaXRaw, maxTranslate));

        if (!isHorizontalSwipe) {
          if (deltaX >= activateThreshold && deltaX >= deltaY * 0.2) {
            isHorizontalSwipe = true;
            if (event instanceof TouchEvent) {
              event.preventDefault();
            }
          }
        }

        if (isHorizontalSwipe && event instanceof TouchEvent) {
          event.preventDefault();
        }

        if (deltaX <= 0) {
          messageBox.style.transform = 'translateX(0px)';
          delete messageBox.dataset.swipe;
          return;
        }

        if (deltaX > activateThreshold) {
          messageBox.dataset.swipe = 'active';
        } else {
          delete messageBox.dataset.swipe;
        }

        messageBox.style.transform = isHorizontalSwipe ? `translateX(${deltaX}px)` : 'translateX(0px)';

        if (!isHorizontalSwipe) {
          return;
        }
      };

      const onEnd = () => {
        if (!isDragging) {
          return;
        }

        const deltaX = Math.max(0, Math.min(currentX - startX, maxTranslate));
        const deltaY = Math.abs(currentY - startY);
        const shouldReply = isHorizontalSwipe && deltaX > triggerThreshold && deltaX > deltaY * 0.6;

        reset();

        if (shouldReply) {
          onReply(index);
        }
      };

      container.addEventListener('touchstart', onStart, { passive: true });
      container.addEventListener('touchmove', onMove, { passive: false });
      container.addEventListener('touchend', onEnd);
      container.addEventListener('mousedown', onStart);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);

      cleanups.push(() => {
        container.removeEventListener('touchstart', onStart);
        container.removeEventListener('touchmove', onMove);
        container.removeEventListener('touchend', onEnd);
        container.removeEventListener('mousedown', onStart);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onEnd);
      });
    });

    return () => {
      cleanups.forEach(fn => fn());
    };
  }, [messages, enabled, onReply]);
}
